import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Users, Coins, Repeat, Award } from "lucide-react";
import { useSiteT } from "../hooks/useSiteT";

const SITE_URL = "https://adrise-agency.com";

const TIERS = [
    { name: "Bronze", range: "1–3", commission: "20%", perks: ["rf.p.monthly", "rf.p.first12", "rf.p.usdt"] },
    { name: "Silver", range: "4–10", commission: "25%", perks: ["rf.p.monthly", "rf.p.lifetime", "rf.p.usdt", "rf.p.priority"] },
    { name: "Gold", range: "10+", commission: "30%", perks: ["rf.p.monthlyLifetime", "rf.p.usdt", "rf.p.comarketing", "rf.p.rep"] },
];

export default function ReferralPage() {
    const { t } = useSiteT();
    return (
        <main
            data-testid="referral-page"
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title="Adrise Affiliate / Referral Program — Up to 30% Recurring"
                description="Refer media buyers and brands to Adrise. Earn up to 30% recurring monthly commission on every account, paid in USDT, lifetime."
                canonical={`${SITE_URL}/referral`}
                keywords={[
                    "agency ad accounts referral",
                    "affiliate program",
                    "media buyer affiliate",
                    "adrise referral",
                ]}
            />

            <Navbar />

            <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
                <div className="container-skala relative">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 mb-6">
                        <Award className="w-3.5 h-3.5 text-primary" />
                        <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary">
                            {t("rf.badge")}
                        </span>
                    </div>
                    <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
                        {t("rf.h1a")}{" "}
                        <span className="text-gradient-primary">{t("rf.h1b")}</span>
                    </h1>
                    <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-2xl">
                        {t("rf.sub1")}{" "}
                        <strong className="text-foreground">{t("rf.sub2")}</strong>{" "}
                        {t("rf.sub3")}
                    </p>
                </div>
            </section>

            {/* Tiers */}
            <section className="pb-16 md:pb-20">
                <div className="container-skala">
                    <div className="grid md:grid-cols-3 gap-4">
                        {TIERS.map((tier, i) => (
                            <article
                                key={tier.name}
                                data-testid={`referral-tier-${tier.name.toLowerCase()}`}
                                className={`rounded-2xl border bg-card p-7 ${
                                    i === 1 ? "border-primary/40 glow-primary" : "border-white/[0.08]"
                                }`}
                            >
                                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">
                                    {tier.range} {t("rf.referrals")}
                                </p>
                                <h3 className="font-display font-bold text-2xl mb-3">
                                    {tier.name}
                                </h3>
                                <div className="flex items-baseline gap-1 mb-5">
                                    <span className="font-display font-black text-5xl">
                                        {tier.commission}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        {t("rf.recurring")}
                                    </span>
                                </div>
                                <ul className="space-y-2 text-sm text-foreground/85">
                                    {tier.perks.map((p) => (
                                        <li key={p} className="flex items-start gap-2">
                                            <Coins className="w-3.5 h-3.5 text-[#2FA57C] mt-0.5 shrink-0" />
                                            <span>{t(p)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-16 border-y border-white/5 bg-white/[0.01]">
                <div className="container-skala">
                    <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-10 max-w-2xl">
                        {t("rf.howTitle")}
                    </h2>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { Icon: Users, title: t("rf.s1t"), text: t("rf.s1") },
                            { Icon: Repeat, title: t("rf.s2t"), text: t("rf.s2") },
                            { Icon: Coins, title: t("rf.s3t"), text: t("rf.s3") },
                        ].map((s) => (
                            <div key={s.title} className="bg-card border border-white/[0.08] rounded-2xl p-7">
                                <s.Icon className="w-5 h-5 text-primary mb-4" />
                                <h3 className="font-display font-semibold text-xl mb-2">{s.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 md:py-20">
                <div className="container-skala text-center">
                    <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4">
                        {t("rf.ctaTitle")}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto mb-7">
                        {t("rf.ctaSub")}
                    </p>
                    <Link to="/signup" data-testid="referral-cta-apply">
                        <Button
                            size="lg"
                            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-7"
                        >
                            {t("rf.apply")}
                        </Button>
                    </Link>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
